// src/views/HomeView.tsx
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Building2, Monitor, Globe2, User, Settings, LogOut } from "lucide-react";
import { LoginSignin } from "../components/LoginSignin";
import { Auth0QuickTest } from "../components/Auth0QuickTest";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
// no permissions helpers needed; gating handled by caps/managedStructures

// Reuse your app-level ClickRole type
export type ClickRole = "Structure Manager" | "Portal Manager";

interface HomeViewProps {
  onLogin: (role: ClickRole) => void;
  loginModalRole: ClickRole | null;
  onModalCancel: () => void;
  onModalSignIn: () => void;
  currentUser?: any | null;
  userProfile?: any | null;
  caps?: { isAdmin: boolean; isPortalManager: boolean } | null;
  homeMode?: "login" | "chooser";
  hasManagedStructures?: boolean;
  auth0Only?: boolean;
  onQuickAccess?: () => void | Promise<void>;
  onOpenTravelersHub?: () => void;
  onOpenVisitAdminTest?: () => void;
  onLogout?: () => void;
  showAuth0Test?: boolean;
}

export default function HomeView({
  onLogin,
  loginModalRole,
  onModalCancel,
  onModalSignIn,
  currentUser,
  userProfile,
  caps,
  homeMode = "login",
  hasManagedStructures = false,
  auth0Only = false,
  onQuickAccess,
  onOpenTravelersHub,
  onOpenVisitAdminTest,
  onLogout,
  showAuth0Test = false,
}: HomeViewProps) {
  // No noisy logs here; logging handled on action (access structure dashboard)
  const isAdmin = (userProfile?.role || '').toUpperCase() === 'ADMIN';
  const isPortalOrAdmin = !!(caps?.isPortalManager || caps?.isAdmin);
  const canUseStructure = !!currentUser && hasManagedStructures;
  const canUsePortal = !!currentUser && isPortalOrAdmin && hasManagedStructures;
  const showChooser = !!currentUser && homeMode === "chooser";
  const welcomeCaption = `Benvenuto ${currentUser?.email || ""}`.trim() + ", procedi alla dashboard";
  const [quickLabel, setQuickLabel] = useState("ATTENDERE");

  useEffect(() => {
    if (!auth0Only || showChooser) return;
    setQuickLabel("ATTENDERE...");
    const t = window.setTimeout(() => {
      setQuickLabel("REINDIRIZZANDO ALLA DASHBOARD...");
    }, 6000);
    return () => window.clearTimeout(t);
  }, [auth0Only, showChooser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-4xl mx-auto space-y-8">

        {/* Brand and Main Title */}
        <div className="text-center">
          <div className="mb-6">
            <div className="mx-auto mb-6 w-fit">
              {/* In Vite, /public files are available at root path */}
              <img src="/travelin-logo.svg" alt="Travelin Logo" className="h-16 w-auto mx-auto" />
            </div>
            <div className="mb-4">
              <p className="text-l text-muted-foreground">Dashboard di accessibilità</p>
            </div>
          </div>
          {/* Profile Header - Show when logged in */}
          {currentUser && (
            <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-travelin flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {currentUser.email || 'Utente'}
                    </p>

                  </div>


                </div>
                <div className="flex items-center gap-4">
                  <div >
                    <p className="font-sm text-gray-600">
                      {userProfile?.role || 'Utente'}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Settings className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem disabled>
                        <User className="w-4 h-4 mr-2" />
                        Impostazioni profilo
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {isAdmin && onOpenVisitAdminTest && (
                        <DropdownMenuItem onClick={onOpenVisitAdminTest}>
                          <Globe2 className="w-4 h-4 mr-2" />
                          Test Visit Bergamo
                        </DropdownMenuItem>
                      )}
                      {onLogout && (
                        <DropdownMenuItem onClick={onLogout} className="text-red-600">
                          <LogOut className="w-4 h-4 mr-2" />
                          Esci
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          )}
        </div>

        {!showChooser && !auth0Only && (
          <div className="w-full max-w-md mx-auto">
            <LoginSignin
              role="Structure Manager"
              onCancel={onModalCancel}
              onSignIn={onModalSignIn}
              variant="inline"
              hideClose
            />
          </div>
        )}

        {!showChooser && auth0Only && (
          <div className="w-full max-w-md mx-auto">
            <LoginSignin
              role="Structure Manager"
              onCancel={onModalCancel}
              onSignIn={onModalSignIn}
              variant="inline"
              hideClose
              mode="quick-access"
              caption={welcomeCaption}
              quickAccessLabel={quickLabel}
              onQuickAccess={onQuickAccess}
            />
          </div>
        )}

        {/* Login Options */}
  {showChooser && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-3xl mx-auto justify-center items-center">
          {/* Structure Manager Login */}
          <Card className={`hover:shadow-xl transition-all duration-300 cursor-pointer group border-0 shadow-lg card-gradient flex flex-col ${!canUseStructure && currentUser ? 'opacity-50' : ''}`}>
            <CardHeader className="text-center pb-4 flex-grow">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-travelin flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-xl text-foreground">Gestore Struttura</CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                Monitora e migliora l'accessibilità delle strutture
              </CardDescription>
              {currentUser && !canUseStructure && (
                <div className="mt-2">
                  <p className="text-ss text-red-600 bg-red-50 px-2 py-1 rounded">
                    Nessuna struttura assegnata
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-0 mt-auto">
              <Button
                className="w-full h-11 bg-gradient-travelin hover:bg-gradient-travelin-cool text-white border-0 shadow-md hover:shadow-lg transition-all duration-300"
                onClick={() => onLogin("Structure Manager")}
                disabled={!canUseStructure}
              >
                Apri la dashboard strutture
              </Button>
            </CardContent>
          </Card>

          {/* Portal Manager Login */}
          {isPortalOrAdmin && (
            <Card className={`hover:shadow-xl transition-all duration-300 cursor-pointer group border-0 shadow-lg card-gradient-orange flex flex-col ${!canUsePortal && currentUser ? 'opacity-50' : ''}`}>
              <CardHeader className="text-center pb-4 flex-grow">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-travelin-warm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Monitor className="w-10 h-10 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Gestore Portale</CardTitle>
                <CardDescription className="text-center text-muted-foreground">
                  Supervisiona operazioni del portale, accessi utenti e integrazioni di sistema
                </CardDescription>
                {currentUser && !canUsePortal && (
                  <div className="mt-2">
                    <p className="text-ss text-red-600 bg-red-50 px-2 py-1 rounded">
                      Strutture gestite richieste
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-0 mt-auto">
                <Button
                  className="w-full h-11 bg-gradient-travelin-warm hover:opacity-90 text-white border-0 shadow-md hover:shadow-lg transition-all duration-300"
                  onClick={() => onLogin("Portal Manager")}
                  disabled={!canUsePortal}
                >
                  Apri la dashboard portale
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Traveler's Hub (public) */}
          {/* <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer group border-0 shadow-lg bg-green-50/50 flex flex-col">
            <CardHeader className="text-center pb-4 flex-grow">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-travelin-green flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Globe2 className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-xl text-foreground">Hub dei viaggiatori</CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                Sfoglia tutte le strutture pubbliche e apri il loro Passaporto di Accessibilità
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 mt-auto">
              <Button
                className="w-full h-11 btn-gradient-green text-white border-0 shadow-md hover:shadow-lg transition-all duration-300"
                onClick={() => onOpenTravelersHub && onOpenTravelersHub()}
              >
                Apri Hub dei viaggiatori
              </Button>
            </CardContent>
          </Card> */}
          </div>
        )}

        {/* Auth0 Testing Section (Development Only) */}
        {showAuth0Test && (
          <div className="w-full max-w-2xl mx-auto">
            <Auth0QuickTest />
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-s text-muted-foreground bg-white/70 backdrop-blur-sm rounded-lg p-4 shadow-sm">
          <p>Hai bisogno di aiuto? Contatta l'amministratore di sistema</p>
        </div>
      </div>
    </div>
  );
}
